/**
 * V49 — Admin Orientation Guard
 *
 * Wrap the admin dashboard with this component. It does NOTHING in:
 *   • Auto mode
 *   • On wide viewports (>= 1024 px — the desktop grid layout works in any rotation)
 *
 * Otherwise it renders a full-screen overlay asking the admin to rotate the
 * device to the configured orientation. Tapping the overlay does NOT
 * dismiss it — only physically rotating the device does. This is
 * intentional: the dashboard should always show its best layout.
 */
import { RotateCcw, Smartphone } from "lucide-react";
import { useAdminOrientation, useDeviceOrientation, ORIENTATION_LABELS } from "../../lib/adminOrientation";

export default function AdminOrientationGuard({ children }) {
    const { mode } = useAdminOrientation();
    const { isLandscape, isMobile } = useDeviceOrientation();

    // Auto mode → never block. Desktop → never block (the desktop layout is
    // always tested and works in either rotation).
    if (mode === "auto" || !isMobile) {
        return children;
    }

    const targetIsLandscape = mode === "landscape";
    const mismatch = targetIsLandscape ? !isLandscape : isLandscape;

    return (
        <>
            {children}
            {mismatch && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Device rotation required"
                    data-testid="admin-orientation-overlay"
                    data-orientation-mode={mode}
                    className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-6 text-center"
                >
                    <div className="max-w-sm w-full bg-white rounded-3xl p-6 sm:p-8 shadow-2xl">
                        <div className="mx-auto w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-4 animate-pulse">
                            <RotateCcw className="w-9 h-9 text-amber-700" />
                        </div>
                        <h2 className="font-heading text-xl font-bold text-[#0F172A] mb-2">
                            Please rotate your device
                        </h2>
                        <p className="text-sm text-slate-600 mb-4">
                            Your admin dashboard is currently set to{" "}
                            <span className="font-semibold text-[#1E3A8A]">
                                {ORIENTATION_LABELS[mode]}
                            </span>
                            . Rotate your device to {targetIsLandscape ? "landscape" : "portrait"} for
                            the best experience.
                        </p>
                        <div className="inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-full">
                            <Smartphone className="w-3.5 h-3.5" /> You can change this in Settings →
                            Display Settings.
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
